import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Globe, Loader2, FileText, ExternalLink, Play, Search, X, Sparkles, ArrowRight, RotateCcw, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import LLMSelectionModal from "@/components/modals/LLMSelectionModal";
import ReAnalyzeModal from "@/components/modals/ReAnalyzeModal";
import type { WorkspaceVendor, EvaluationStatus, LLMModelId } from "@/services/api";

interface VendorsTableProps {
  vendors: WorkspaceVendor[];
  isLoading: boolean;
  onEvaluate: (vendorId: string, model?: LLMModelId) => void;
  onReAnalyze?: (vendorId: string, maxPages: number) => void;
  onRemove?: (vendorId: string, vendorName: string) => void;
  evaluatingVendorId: string | null;
  isAdmin?: boolean;
}

function getStatusBadge(
  status: EvaluationStatus | undefined,
  hasEvaluation: boolean,
  isEvaluating: boolean,
  vendorStatus?: string
) {
  // Show "Analyzing..." if the vendor is still being crawled
  if (vendorStatus === "processing") {
    return (
      <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700">
        <Loader2 className="w-3 h-3 animate-spin" />
        Analyzing...
      </span>
    );
  }

  // Show error if vendor crawl failed
  if (vendorStatus === "error" && !hasEvaluation) {
    return (
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700">
        Analysis Failed
      </span>
    );
  }

  // Show "Evaluating..." if we just started or if status indicates in progress
  if (isEvaluating || status === "pending" || status === "processing") {
    return (
      <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
        <Loader2 className="w-3 h-3 animate-spin" />
        Evaluating...
      </span>
    );
  }

  if (status === "completed") {
    return (
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700">
        Evaluated
      </span>
    );
  }

  if (status === "failed") {
    return (
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700">
        Failed
      </span>
    );
  }

  // No evaluation
  return (
    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-muted text-muted-foreground">
      Not Evaluated
    </span>
  );
}

export default function VendorsTable({
  vendors,
  isLoading,
  onEvaluate,
  onReAnalyze,
  onRemove,
  evaluatingVendorId,
  isAdmin = false,
}: VendorsTableProps) {
  const navigate = useNavigate();
  const [showReportModal, setShowReportModal] = useState(false);
  const [selectedVendor, setSelectedVendor] = useState<WorkspaceVendor | null>(null);
  const [showLLMModal, setShowLLMModal] = useState(false);
  const [llmModalVendorId, setLLMModalVendorId] = useState<string | null>(null);
  const [showReAnalyzeModal, setShowReAnalyzeModal] = useState(false);
  const [reAnalyzeVendor, setReAnalyzeVendor] = useState<WorkspaceVendor | null>(null);

  const handleViewReport = (vendor: WorkspaceVendor) => {
    setSelectedVendor(vendor);
    setShowReportModal(true);
  };

  const handleEvaluateClick = (vendorId: string) => {
    if (isAdmin) {
      setLLMModalVendorId(vendorId);
      setShowLLMModal(true);
    } else {
      onEvaluate(vendorId);
    }
  };

  const handleLLMConfirm = (model: LLMModelId) => {
    if (llmModalVendorId) {
      onEvaluate(llmModalVendorId, model);
    }
    setShowLLMModal(false);
    setLLMModalVendorId(null);
  };

  const handleReportTypeSelect = (reportType: 'static' | 'interactive') => {
    if (!selectedVendor) return;
    
    if (reportType === 'static') {
      navigate(`/reports/static/${selectedVendor.evaluation_id}`);
    } else {
      navigate(`/reports/experience/${selectedVendor.evaluation_id}`);
    }
    setShowReportModal(false);
    setSelectedVendor(null);
  };

  const getActionButton = (vendor: WorkspaceVendor) => {
    const isEvaluating = evaluatingVendorId === vendor.vendor_id;
    const isInProgress = vendor.evaluation_status === "pending" || vendor.evaluation_status === "processing";
    const isAnalyzing = vendor.vendor_status === "processing";

    // Vendor is still being crawled - show disabled button
    if (isAnalyzing) {
      return (
        <Button
          variant="outline"
          size="sm"
          disabled
          className="flex items-center gap-1 w-[140px] justify-center"
        >
          <Loader2 className="w-3 h-3 animate-spin" />
          Analyzing...
        </Button>
      );
    }

    // Completed evaluation - show View Report (+ Rerun for admins)
    if (vendor.evaluation_status === "completed") {
      return (
        <div className="flex items-center gap-2 justify-end">
          {isAdmin && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => handleEvaluateClick(vendor.vendor_id)}
              className="flex items-center gap-1 text-muted-foreground hover:text-foreground"
              title="Rerun Evaluation"
            >
              <RotateCcw className="w-3 h-3" />
              Rerun
            </Button>
          )}
          <Button
            variant="outline"
            size="sm"
            className="flex items-center gap-1 w-[140px] justify-center"
            onClick={() => handleViewReport(vendor)}
          >
            <FileText className="w-3 h-3" />
            View Report
          </Button>
        </div>
      );
    }

    // Failed evaluation - show Rerun
    if (vendor.evaluation_status === "failed") {
      return (
        <Button
          variant="outline"
          size="sm"
          onClick={() => handleEvaluateClick(vendor.vendor_id)}
          className="flex items-center gap-1 w-[140px] justify-center"
        >
          <RotateCcw className="w-3 h-3" />
          Rerun Evaluation
        </Button>
      );
    }

    // Analysis failed - show Re-Analyze for admins, disabled Evaluate for non-admins
    const isAnalysisFailed = vendor.vendor_status === "error";
    if (isAnalysisFailed && isAdmin && onReAnalyze) {
      return (
        <Button
          variant="outline"
          size="sm"
          onClick={() => {
            setReAnalyzeVendor(vendor);
            setShowReAnalyzeModal(true);
          }}
          className="flex items-center gap-1 w-[140px] justify-center"
        >
          <RotateCcw className="w-3 h-3" />
          Re-Analyze
        </Button>
      );
    }

    // Show Evaluate button - disabled if evaluation is in progress or analysis failed
    return (
      <Button
        variant="brand"
        size="sm"
        onClick={() => handleEvaluateClick(vendor.vendor_id)}
        disabled={isEvaluating || isInProgress || isAnalysisFailed}
        className="flex items-center gap-1 w-[140px] justify-center"
      >
        <Play className="w-3 h-3" />
        Evaluate
      </Button>
    );
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-primary mr-2" />
        <span className="text-muted-foreground">Loading vendors...</span>
      </div>
    );
  }

  if (vendors.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 px-4">
        <div className="w-12 h-12 rounded-full bg-muted flex items-center justify-center mb-3">
          <Search className="w-6 h-6 text-muted-foreground" />
        </div>
        <h3 className="text-lg font-semibold text-foreground mb-1">No vendors found</h3>
        <p className="text-sm text-muted-foreground text-center">
          Try adjusting your search terms.
        </p>
      </div>
    );
  }

  return (
    <>
      <div className="border border-border rounded-lg overflow-x-auto">
        <table className="w-full min-w-[600px]">
          <thead className="bg-muted/50">
            <tr>
              <th className="px-4 py-3 text-left text-sm font-semibold text-foreground">
                Vendor
              </th>
              <th className="px-4 py-3 text-left text-sm font-semibold text-foreground">
                Website
              </th>
              <th className="px-4 py-3 text-left text-sm font-semibold text-foreground">
                Status
              </th>
              <th className="px-4 py-3 text-right text-sm font-semibold text-foreground">
                Action
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {vendors.map((vendor) => (
              <tr key={vendor.vendor_id} className="hover:bg-muted/30 transition-colors">
                <td className="px-4 py-3">
                  <div className="flex items-center gap-3">
                    <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-primary/10">
                      <Globe className="w-4 h-4 text-primary" />
                    </div>
                    <span className="font-medium text-foreground">{vendor.vendor_name}</span>
                  </div>
                </td>
                <td className="px-4 py-3">
                  {vendor.vendor_domain ? (
                    <a
                      href={vendor.vendor_domain.startsWith('http') ? vendor.vendor_domain : `https://${vendor.vendor_domain}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-muted-foreground hover:text-primary flex items-center gap-1"
                    >
                      {vendor.vendor_domain.replace(/^https?:\/\//, '')}
                      <ExternalLink className="w-3 h-3" />
                    </a>
                  ) : (
                    <span className="text-sm text-muted-foreground">—</span>
                  )}
                </td>
                <td className="px-4 py-3">
                  {getStatusBadge(vendor.evaluation_status, vendor.has_evaluation, evaluatingVendorId === vendor.vendor_id, vendor.vendor_status)}
                </td>
                <td className="px-4 py-3 text-right">
                  <div className="flex items-center justify-end gap-2">
                    {getActionButton(vendor)}
                    {isAdmin && onRemove && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => onRemove(vendor.vendor_id, vendor.vendor_name)}
                        className="text-muted-foreground hover:text-destructive"
                        title="Remove vendor"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* LLM Selection Modal (Admin only) */}
      <LLMSelectionModal
        isOpen={showLLMModal}
        onClose={() => {
          setShowLLMModal(false);
          setLLMModalVendorId(null);
        }}
        onConfirm={handleLLMConfirm}
        title="Select Evaluation Model"
      />

      {/* Re-Analyze Modal (Admin only) */}
      <ReAnalyzeModal
        isOpen={showReAnalyzeModal}
        onClose={() => {
          setShowReAnalyzeModal(false);
          setReAnalyzeVendor(null);
        }}
        onConfirm={(maxPages) => {
          if (reAnalyzeVendor && onReAnalyze) {
            onReAnalyze(reAnalyzeVendor.vendor_id, maxPages);
          }
          setShowReAnalyzeModal(false);
          setReAnalyzeVendor(null);
        }}
        vendorName={reAnalyzeVendor?.vendor_name ?? ""}
        vendorDomain={reAnalyzeVendor?.vendor_domain ?? ""}
      />

      {/* Report Type Selection Modal */}
      {showReportModal && selectedVendor && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
          <div className="bg-card border border-border rounded-xl shadow-2xl max-w-md w-full mx-4 overflow-hidden">
            {/* Header with gradient */}
            <div className="bg-gradient-to-r from-[#063F6B] to-[#124C77] p-6">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-semibold text-white">View Report</h2>
                  <p className="text-sm text-white/70 mt-1">{selectedVendor.vendor_name}</p>
                </div>
                <button
                  onClick={() => {
                    setShowReportModal(false);
                    setSelectedVendor(null);
                  }}
                  className="text-white/70 hover:text-white transition-colors p-2.5 rounded-lg hover:bg-white/10 min-w-[44px] min-h-[44px] flex items-center justify-center"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>
            
            <div className="p-6">
              <p className="text-sm text-muted-foreground mb-6">
                Choose how you'd like to view the evaluation report:
              </p>
              <div className="space-y-3">
                <button
                  onClick={() => handleReportTypeSelect('static')}
                  className="w-full p-4 text-left border-2 border-border rounded-xl hover:border-primary/50 hover:bg-primary/5 transition-all group"
                >
                  <div className="flex items-center gap-4">
                    <div className="flex items-center justify-center w-12 h-12 rounded-xl bg-gradient-to-br from-[#063F6B] to-[#124C77] shadow-lg">
                      <FileText className="w-6 h-6 text-white" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-foreground group-hover:text-primary transition-colors">
                        Standard Report
                      </h3>
                      <p className="text-sm text-muted-foreground">
                        Detailed analysis in classic format
                      </p>
                    </div>
                    <ArrowRight className="w-5 h-5 text-muted-foreground group-hover:text-primary group-hover:translate-x-1 transition-all" />
                  </div>
                </button>
                
                <button
                  onClick={() => handleReportTypeSelect('interactive')}
                  className="w-full p-4 text-left border-2 border-border rounded-xl hover:border-[#D4893E]/50 hover:bg-[#D4893E]/5 transition-all group"
                >
                  <div className="flex items-center gap-4">
                    <div className="flex items-center justify-center w-12 h-12 rounded-xl bg-gradient-to-br from-[#D4893E] to-[#C07830] shadow-lg">
                      <Sparkles className="w-6 h-6 text-white" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-foreground group-hover:text-[#D4893E] transition-colors">
                        Interactive Experience
                      </h3>
                      <p className="text-sm text-muted-foreground">
                        Immersive animated presentation
                      </p>
                    </div>
                    <ArrowRight className="w-5 h-5 text-muted-foreground group-hover:text-[#D4893E] group-hover:translate-x-1 transition-all" />
                  </div>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
